/**
 * @nostr-post/wiki - Entity resolver
 *
 * Multiple pubkeys can publish kind:30818 events with the same d-tag.
 * The resolver picks the "canonical" event from the set.
 *
 * Default strategy: defer-aware, newest-wins.
 *   - Events that carry a `defer` marker on an `a` or `e` tag are excluded
 *     from the candidate pool (they explicitly yield to another version).
 *   - If all events defer, fall back to the full set (newest wins).
 */

export interface WikiEvent {
  id: string;
  pubkey: string;
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
  sig?: string;
}

export type WikiResolverFunction = (events: WikiEvent[]) => WikiEvent | null;

/**
 * Default resolver: defer-aware newest-wins.
 *
 * An event "defers" when any of its `a` or `e` tags carries the marker
 * `"defer"` at index 3 (NIP-54 fork/defer convention).
 */
export const defaultResolver: WikiResolverFunction = (events: WikiEvent[]): WikiEvent | null => {
  if (events.length === 0) return null;

  // Collect IDs of events that explicitly defer to another version
  const deferredIds = new Set(
    events
      .filter((e) => e.tags.some((t) => (t[0] === 'a' || t[0] === 'e') && t[3] === 'defer'))
      .map((e) => e.id)
  );

  const candidates = events.filter((e) => !deferredIds.has(e.id));
  const pool = candidates.length > 0 ? candidates : events;

  return pool.reduce<WikiEvent | null>(
    (newest, event) => (newest === null || event.created_at > newest.created_at ? event : newest),
    null
  );
};

/**
 * Build the full set of `a` tag values for all events in the pool.
 * Used to perform the second-hop query: fetch all reviews that cited any version.
 *
 * Returns strings in the form "30818:<pubkey>:<d-tag>".
 */
export function collectEntityATags(events: WikiEvent[]): string[] {
  const seen = new Set<string>();
  for (const event of events) {
    const dTag = event.tags.find((t) => t[0] === 'd')?.[1];
    if (dTag) {
      seen.add(`30818:${event.pubkey}:${dTag}`);
    }
  }
  return [...seen];
}

/**
 * Group events by their d-tag. Events without a d-tag are grouped under ''.
 * The resolver assumes every event it receives shares one d-tag, so callers
 * that may receive several entities (search results, `#i` lookups) should
 * group first and resolve each group separately.
 */
export function groupByDTag(events: WikiEvent[]): Map<string, WikiEvent[]> {
  const groups = new Map<string, WikiEvent[]>();
  for (const event of events) {
    const dTag = event.tags.find((t) => t[0] === 'd')?.[1] ?? '';
    const group = groups.get(dTag);
    if (group) group.push(event);
    else groups.set(dTag, [event]);
  }
  return groups;
}

/** Number of distinct pubkeys that published a version of the entity. */
export function countContributors(events: WikiEvent[]): number {
  return new Set(events.map((e) => e.pubkey)).size;
}

/**
 * From events that may span several entities, return the d-tag of the entity
 * whose resolved winner is newest. Events without a d-tag are ignored.
 */
export function selectNewestEntity(
  events: WikiEvent[],
  resolver: WikiResolverFunction = defaultResolver
): string | undefined {
  let best: { dTag: string; createdAt: number } | undefined;
  for (const [dTag, group] of groupByDTag(events)) {
    if (!dTag) continue;
    const winner = resolver(group);
    if (winner && (!best || winner.created_at > best.createdAt)) {
      best = { dTag, createdAt: winner.created_at };
    }
  }
  return best?.dTag;
}
